import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Swal from 'sweetalert2';
import config from '../../Config';

export default function AddSubCategoryForm({ category, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire('Error', 'Image must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);

    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileChange(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile) {
      Swal.fire('Error', 'Please upload an image', 'error');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('description', formData.description);
      formPayload.append('isActive', formData.isActive);
      formPayload.append('category', category._id);
      formPayload.append('image', imageFile);

      const response = await fetch(`${config.apiBaseUrl}/admin/upsale/subcategory/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formPayload,
      });

      const data = await response.json();

      if (response.ok) {
        Swal.fire('Success', data.message || 'Subcategory added.', 'success');

        // reset
        setFormData({
          name: '',
          description: '',
          isActive: true,
        });
        setImageFile(null);
        setPreview('');

        onSuccess?.();
      } else {
        Swal.fire('Error', data.message || 'Failed to add subcategory.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Something went wrong.', 'error');
    }

    setLoading(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onCancel} size="small" sx={{ mr: 1 }} disabled={loading}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Add Subcategory for: <strong>{category.name}</strong>
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Name"
            name="name"
            fullWidth
            required
            disabled={loading}
            value={formData.name}
            onChange={handleChange}
          />

          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: '#fafafa',
              height: { xs: 180, sm: 200, md: 220 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              overflow: 'hidden',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: '#f0f7ff',
              },
            }}
          >
            <input
              type="file"
              hidden
              id="add-subcategory-image"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files[0])}
            />

            {!preview ? (
              <label htmlFor="add-subcategory-image">
                <Typography>
                  Drag & Drop or <b>Click to Upload</b>
                </Typography>
              </label>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <img
                    src={preview}
                    alt="preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button component="label" size="small">
                    Replace
                    <input
                      hidden
                      type="file"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                  </Button>

                  <Button
                    color="error"
                    size="small"
                    onClick={() => {
                      setImageFile(null);
                      setPreview('');
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </>
            )}
          </Box>

          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            minRows={2}
            disabled={loading}
            value={formData.description}
            onChange={handleChange}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={handleCheckboxChange}
                name="isActive"
                disabled={loading}
              />
            }
            label="Is Active"
          />

          <Stack direction="row" spacing={2}>
            <Button variant="contained" type="submit" disabled={loading || !imageFile}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Add Subcategory'}
            </Button>

            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
